import { useState, useEffect, useRef } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useWalkthrough } from "./WalkthroughOverlay";
import {
  FileText, UserPlus, Send, Receipt, Users, Globe, Package,
  TrendingUp, Rocket, Share2, Trophy, FolderPlus,
  X, Eye, Sparkles,
} from "lucide-react";

const ICON_MAP = {
  FileText, UserPlus, Send, Receipt, Users, Globe, Package,
  TrendingUp, Rocket, Share2, Trophy, FolderPlus, Sparkles,
};

// Walkthrough steps from backend command_hub library (duplicated for client-side triggering)
const WALKTHROUGH_STEPS = {
  create_quote: [
    { route: "/quotes", testid: "quotes-page", title: "Open Quotes", description: "Your quotes hub — see all quotes, compare pricing, and create new ones.", placement: "bottom" },
    { route: "/quotes", testid: "new-quote-btn", title: "Create New Quote", description: "Click '+ New Quote' to open the builder. Add line items from your catalog, set quantities and pricing.", placement: "bottom" },
    { route: "/catalog", testid: "catalog-page", title: "Your Catalog", description: "Tip: Your catalog items appear as line items in the quote builder. Make sure your catalog is set up first!", placement: "bottom" },
  ],
  create_lead: [
    { route: "/leads", testid: "leads-page", title: "Go to Leads", description: "Your lead management hub. All leads are listed here with filtering and sorting.", placement: "bottom" },
    { route: "/leads", testid: "leads-page", title: "Add New Lead", description: "Click '+ New Lead' at the top. Fill in company name, contact person, estimated value, and project type.", placement: "bottom" },
    { route: "/kanban", testid: "pipeline-dashboard", title: "Track in Pipeline", description: "Your leads appear in the Sales Pipeline. Drag cards between columns: Lead → Qualified → Proposal → Won.", placement: "bottom" },
  ],
  create_proposal: [
    { route: "/proposal-builder", testid: "proposal-builder-list", title: "Open Proposal Builder", description: "Your proposal hub with all drafts, sent proposals, and templates.", placement: "bottom" },
    { route: "/proposal-builder", testid: "create-proposal-btn", title: "Create New Proposal", description: "Click '+ New Proposal' for a drag-and-drop editor with 20+ block types.", placement: "bottom" },
    { route: "/proposal-builder", testid: "proposal-builder-list", title: "Share & Track", description: "Click 'Share' to generate a public link with built-in analytics — views, time per section, and engagement.", placement: "bottom" },
  ],
  create_invoice: [
    { route: "/invoices", testid: "invoices-page", title: "Open Invoices", description: "Your invoice management center. See all invoices with status tracking.", placement: "bottom" },
    { route: "/invoices", testid: "create-invoice-btn", title: "Create Invoice", description: "Click '+ New Invoice' → link to a project or quote → add line items → set payment terms → Preview.", placement: "bottom" },
    { route: "/invoices", testid: "invoices-page", title: "Send & Track", description: "Send via email or share a link. Track status: Draft → Sent → Viewed → Paid/Overdue.", placement: "bottom" },
  ],
  create_project: [
    { route: "/projects", testid: "sidebar-link-projects", title: "Open Projects", description: "Navigate to the Projects section for full project management.", placement: "right" },
    { route: "/projects", testid: "sidebar-link-projects", title: "Create Project", description: "Click '+ New Project' → link to client → set timeline and budget → assign team → Save.", placement: "right" },
    { route: "/projects/gantt", testid: "sidebar-link-projects/gantt", title: "Gantt Chart", description: "Visual timeline planning with task dependencies and milestones.", placement: "right" },
  ],
  sales_pipeline: [
    { route: "/kanban", testid: "pipeline-dashboard", title: "Sales Intelligence Hub", description: "Your pipeline has 4 tabs: Pipeline (Kanban), Funnel, Performance, and Engagement.", placement: "bottom" },
    { route: "/kanban", testid: "intelligence-tabs", title: "Switch Views", description: "Toggle between Kanban, visual funnel, performance metrics, and engagement scoring.", placement: "bottom" },
    { route: "/kanban", testid: "pipeline-stats", title: "AI Deal Scoring", description: "The Engagement tab uses AI to rank leads by conversion probability.", placement: "bottom" },
  ],
  catalog: [
    { route: "/catalog", testid: "catalog-page", title: "Your Catalog", description: "All your equipment, services, and digital products with pricing.", placement: "bottom" },
    { route: "/catalog", testid: "catalog-page", title: "Add Items", description: "Click '+ New Item' → name, category, rate, description → upload images → Save.", placement: "bottom" },
    { route: "/catalog/health", testid: "catalog-health-page", title: "Catalog Health", description: "AI-powered dashboard finds missing images, pricing gaps, and duplicates.", placement: "bottom" },
  ],
  crew: [
    { route: "/crew", testid: "crew-directory-page", title: "Crew Directory", description: "Manage team profiles with skills, availability, rates, and contact info.", placement: "bottom" },
    { route: "/crew/skill-matrix", testid: "skill-search", title: "Skill Matrix", description: "Visual grid showing team capabilities — identify skill gaps.", placement: "bottom" },
  ],
  brand_launch: [
    { route: "/marketing/brand-launch", testid: "brand-launch-page", title: "Brand Launch Wizard", description: "7-step automation pipeline for your entire marketing launch.", placement: "bottom" },
    { route: "/marketing/brand-setup", testid: "brand-setup-page", title: "Step 1: Brand Setup", description: "Set your company name, logo, colors, typography, and brand voice.", placement: "bottom" },
    { route: "/marketing/diagnostic", testid: "brand-diagnostic-page", title: "Step 2: Diagnostics", description: "Run AI brand analyses — SWOT, digital presence, competitor comparison.", placement: "bottom" },
  ],
  social_media: [
    { route: "/marketing/social-hub", testid: "social-content-hub", title: "Social Content Hub", description: "Create and schedule content for all major platforms.", placement: "bottom" },
    { route: "/marketing/social-hub", testid: "hub-tabs", title: "One-Click Week", description: "AI generates a full week of content (15-25 posts) across all platforms.", placement: "bottom" },
  ],
  website_builder: [
    { route: "/marketing/website", testid: "sidebar-link-marketing/website", title: "AI Website Builder", description: "Enter a description — AI generates a 5-7 page website with images.", placement: "right" },
    { route: "/marketing/website", testid: "sidebar-link-marketing/website", title: "34 Templates", description: "Industry-specific templates. All accessible and mobile-responsive.", placement: "right" },
  ],
  roi_tracker: [
    { route: "/marketing/roi", testid: "marketing-roi-page", title: "ROI Tracker", description: "Track marketing spend vs. revenue with automatic attribution.", placement: "bottom" },
    { route: "/invoices", testid: "invoices-page", title: "Revenue Source", description: "When invoices are paid, revenue auto-attributes to the originating campaign.", placement: "bottom" },
  ],
  badges_xp: [
    { testid: "quickwins-widget", title: "Quick Wins", description: "Your gamified checklist — 7 missions that earn XP and unlock badges.", placement: "left" },
    { route: "/settings", testid: "sidebar-link-settings", title: "Badges Tab", description: "Go to Settings → Badges to see all 10 achievements.", placement: "right" },
  ],
  post_onboarding: [
    { route: "/dashboard", testid: "dashboard-page", title: "Mission Control", description: "Your command center. Key metrics, quick actions, and alerts — everything at a glance.", placement: "bottom" },
    { testid: "sidebar-link-leads", title: "Sales Pipeline", description: "Create leads, track deals, and manage your pipeline. Drag cards between stages to move deals forward.", placement: "right" },
    { testid: "sidebar-link-proposal-builder", title: "Proposal Builder", description: "Design proposals with 20+ blocks, send via link, and track engagement analytics.", placement: "right" },
    { testid: "sidebar-link-mailing", title: "Mailing Studio", description: "Send campaigns, build automations, and grow your audience. Your marketing engine.", placement: "right" },
    { testid: "quickwins-widget", title: "Quick Wins", description: "Start here! Complete 7 missions to earn XP and master the platform. Takes ~15 minutes.", placement: "left" },
  ],
};

export function WalkthroughNudge() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const walkthrough = useWalkthrough();
  const [nudge, setNudge] = useState(null);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timeoutRef = useRef(null);
  const tourTriggeredRef = useRef(false);

  // Auto-trigger post-onboarding product tour when ?tour=1
  useEffect(() => {
    if (searchParams.get("tour") === "1" && !tourTriggeredRef.current) {
      tourTriggeredRef.current = true;
      // Remove ?tour=1 from URL cleanly
      searchParams.delete("tour");
      setSearchParams(searchParams, { replace: true });
      // Delay to let the dashboard render first
      const tourTimer = setTimeout(() => {
        const steps = WALKTHROUGH_STEPS.post_onboarding;
        if (steps?.length) walkthrough.start(steps);
      }, 1500);
      return () => clearTimeout(tourTimer);
    }
  }, [searchParams]);

  useEffect(() => {
    const path = location.pathname;
    // Reset state on every path change (including initial mount)
    setNudge(null);
    setVisible(false);
    setDismissed(false);

    // Delay the nudge check so page content loads first
    timeoutRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/walkthrough/suggest?page=${encodeURIComponent(path)}`);
        if (r.data.show && r.data.nudge) {
          setNudge(r.data.nudge);
          setTimeout(() => setVisible(true), 100);
        }
      } catch {
        // Not authenticated or error — silent
      }
    }, 1200);

    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [location.pathname]);

  const handleDismiss = async () => {
    setVisible(false);
    setTimeout(() => setDismissed(true), 300);
    if (nudge?.topic) {
      try { await api.post("/walkthrough/dismiss-nudge", { topic: nudge.topic }); } catch {}
    }
  };

  const handleShowMe = () => {
    const steps = WALKTHROUGH_STEPS[nudge?.topic];
    if (steps && walkthrough) {
      walkthrough.start(steps);
    }
    handleDismiss();
  };

  if (!nudge || dismissed) return null;

  const Icon = ICON_MAP[nudge.icon] || Sparkles;

  return (
    <div
      className={`fixed top-16 left-1/2 -translate-x-1/2 z-[200] max-w-md w-[calc(100%-2rem)] transition-all duration-300 ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3"
      }`}
      data-testid="walkthrough-nudge"
    >
      <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-[var(--app-bg-secondary)]/95 backdrop-blur-xl border border-[var(--app-accent)]/20 shadow-2xl">
        <div className="w-9 h-9 rounded-xl bg-[var(--app-accent)]/10 flex items-center justify-center flex-shrink-0">
          <Icon size={18} className="text-[var(--app-accent)]" />
        </div>
        <p className="text-sm text-[var(--app-text)] flex-1 leading-snug">{nudge.message}</p>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <button
            onClick={handleShowMe}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[var(--app-accent)] text-white text-xs font-semibold hover:bg-[var(--app-accent-hover)] transition-all"
            data-testid="nudge-show-me-btn"
          >
            <Eye size={12} /> Show Me
          </button>
          <button
            onClick={handleDismiss}
            className="p-1.5 rounded-lg text-[var(--app-text-faint)] hover:text-[var(--app-text-muted)] hover:bg-[var(--app-surface)] transition-all"
            data-testid="nudge-dismiss-btn"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
